/**
 * sanitizeEditorHtml — DOMPurify-Wrapper mit Whitelist fuer TipTap-Output.
 *
 * XSS-Schutz: TipTap-Output kann beliebige HTML-Tags enthalten (z.B. <script>).
 * Vor dem Persistieren in DB MUSS der Output sanitized werden.
 *
 * Whitelist:
 *   - Tags:  p, strong, em, ul, ol, li, a, br, blockquote, span, img,
 *            table, thead, tbody, tfoot, tr, th, td (Tabellen, Phase 8 Iter 3)
 *   - Attrs: href (auf a), data-variable (auf span — variableChip), style (Inline-CSS),
 *            src/alt/width/height (auf img — externe Bilder fuer Signatur-Logos),
 *            colspan/rowspan (auf th/td)
 *
 * Bild-Sicherheit (img):
 *   - Erlaubt: src="https://..." (externe URL) ODER src="data:image/(png|jpeg|gif|webp);base64,..."
 *   - VERBOTEN: data:image/svg+xml (kann <script> enthalten — XSS-Vektor)
 *   - VERBOTEN: javascript:, http://, relative URLs, beliebige andere Schemata
 *   - Hook entfernt das ganze <img>-Element wenn src nicht zur Whitelist passt.
 *
 * Whitespace-Preservation (Bug A 29.05. + Bug D 30.05.):
 *   - HTML kollabiert Multi-Spaces normalerweise auf 1 Space → Signatur-Tabellen
 *     mit `|`-Strichen, die der User per Spaces ausgerichtet hat, rutschen zusammen.
 *   - Pre-Sanitize-Step (DOMParser-basiert): Multi-Spaces (>=2) in TextNodes durch
 *     1 Space + (N-1) U+00A0 (nbsp) ersetzen. nbsp wird vom Browser/Mail-Client NICHT
 *     kollabiert → tabellarische Ausrichtung bleibt erhalten.
 *   - Bug D (30.05.): TextNodes INNERHALB von `<a>` werden ausgelassen — nbsp im
 *     Link-Tag wurde mit-unterstrichen ("anstoss24" hat einen Strich rechts).
 *
 * Pure function — Vitest-tauglich (jsdom).
 */
import DOMPurify from 'dompurify';

// Whitelist-Pattern fuer img.src:
//   - https://... (extern gehostet)
//   - data:image/(png|jpeg|gif|webp);base64,... (inline-embedded, NICHT svg+xml)
const ALLOWED_IMG_SRC = /^(https:\/\/|data:image\/(?:png|jpeg|gif|webp);base64,)/i;

const ALLOWED_TAGS = [
  'p',
  'strong',
  'em',
  'u', // Phase 8 Iter 4 — Underline-Mark
  's', // strike — falls Bedarf
  'ul',
  'ol',
  'li',
  'a',
  'br',
  'hr', // Phase 8 Iter 4 — Trennlinie
  'h1',
  'h2',
  'h3',
  'blockquote',
  'span',
  'img',
  // Phase 8 Iter 3: Tabellen-Support fuer tabellarische Signaturen.
  'table',
  'thead',
  'tbody',
  'tfoot',
  'tr',
  'th',
  'td',
];

const ALLOWED_ATTR = [
  'href',
  'data-variable',
  'style',
  'contenteditable',
  'src',
  'alt',
  'width',
  'height',
  // Phase 8 Iter 3: Tabellen-Span-Attribute.
  'colspan',
  'rowspan',
  // Phase 8 Iter 4: Links oeffnen in neuem Tab + Sicherheit
  'target',
  'rel',
];

// Hook: stripped <img>-Tags mit unsicherem src (javascript:, http://, data:image/svg+xml, relative URLs).
// Erlaubt: https:// und data:image/(png|jpeg|gif|webp);base64,...
// Wird einmal beim Modul-Load registriert. DOMPurify ruft Hook PRO Node auf.
// Idempotent: doppelte Registrierung waere ein Memory-Leak, daher Guard via Symbol.
const HOOK_REGISTERED = Symbol.for('libero.sanitizeEditorHtml.imgHook');
const purify = DOMPurify as unknown as { [k: symbol]: true | undefined };
if (!purify[HOOK_REGISTERED]) {
  // DOMPurify entfernt data:-URLs by default. ALLOW_DATA_ATTR / ALLOW_UNKNOWN_PROTOCOLS
  // sind globale Schalter und zu breit. ADD_URI_SAFE_ATTR ist ebenfalls global.
  // Wir nehmen den uponSanitizeAttribute-Hook + ALLOWED_URI_REGEXP im sanitize-Call (siehe unten).
  DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
    if (node.nodeName === 'IMG' && data.attrName === 'src' && !ALLOWED_IMG_SRC.test(data.attrValue ?? '')) {
      // Statt nur das Attribut zu entfernen den ganzen Knoten als unsicher markieren —
      // ein <img> ohne src ist unsinnig.
      data.keepAttr = false;
      data.attrValue = '';
      node.parentNode?.removeChild(node);
    }
  });
  purify[HOOK_REGISTERED] = true;
}

// DOMPurify hat eine eingebaute URI-Validierung die data: standardmaessig BLOCKT.
// Wir muessen sie via ALLOWED_URI_REGEXP erweitern damit unser Hook ueberhaupt drankommt.
// Pattern: alle Schemata die DOMPurify normalerweise zulaesst (https, mailto, tel, ftp, ...)
// PLUS data:image/(png|jpeg|gif|webp);base64,...
// Der uponSanitizeAttribute-Hook (oben) filtert dann noch feiner pro <img>-Element.
const ALLOWED_URI_REGEXP =
  /^(?:(?:https?|mailto|tel|ftps?):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))|^data:image\/(?:png|jpeg|gif|webp);base64,/i;

// U+00A0 (no-break space). String enthaelt das echte nbsp-Zeichen.
const NBSP = ' ';

/**
 * Multi-Spaces (>=2) in TextNodes durch 1 Space + (N-1) nbsp ersetzen.
 *
 * DOMParser-basiert (Bug D, 30.05.): vorher Regex auf `>([^<]+)<`, das hat aber
 * TextNodes innerhalb von `<a>`-Tags mitgenommen — nbsp im Link-Tag wird vom
 * Browser mit-unterstrichen. Jetzt: TreeWalker ueber alle TextNodes, skip wenn
 * Vorfahre `<a>` ist.
 *
 * Fallback ohne DOMParser (z.B. Node ohne jsdom): unveraenderte Eingabe — sicherer
 * als Regex-Heuristik die `<a>` nicht ausnehmen kann.
 */
export function preserveMultiSpaces(html: string): string {
  if (typeof DOMParser === 'undefined') return html;

  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  const body = doc.body;
  if (!body) return html;

  const walker = doc.createTreeWalker(body, NodeFilter.SHOW_TEXT);
  const toReplace: Text[] = [];
  let node: Node | null;
  while ((node = walker.nextNode())) {
    const text = node as Text;
    if (!/ {2,}/.test(text.data)) continue;
    // Bug D: nbsp in `<a>` wird mit-unterstrichen → skip
    if (text.parentElement?.closest('a')) continue;
    toReplace.push(text);
  }
  for (const text of toReplace) {
    text.data = text.data.replace(/ {2,}/g, (spaces) =>
      ' ' + NBSP.repeat(spaces.length - 1),
    );
  }
  return body.innerHTML;
}

export function sanitizeEditorHtml(html: string): string {
  const preserved = preserveMultiSpaces(html);
  return DOMPurify.sanitize(preserved, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOWED_URI_REGEXP,
  });
}
